import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  RdsFileItem,
  RdsFileUploader,
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsUploadComponent,
  RdsUploadErrorDirective,
  RdsUploadIntlService,
} from '@rds/angular-components';
import {
  CATALOG_ATTACHMENT_LIMITS,
  CATALOG_ATTACHMENT_TEXTS,
} from '../../../constants/catalog-form.constants';

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;
const MAX_FILE_SIZE_BYTES = CATALOG_ATTACHMENT_LIMITS.MAX_FILE_SIZE_MB * BYTES_PER_MB;
const DROP_ZONE_SELECTOR = 'button[rds-upload-drop-zone]';
const ERROR_SELECTOR = 'rds-upload-error.catalog-attachments__error';

// One row of the RoSE-style file list.
export interface CatalogAttachmentRow {
  item: RdsFileItem;
  name: string;
  size: string;
  // Object URL for an image preview; null for other file types.
  thumbnailUrl: string | null;
}

// RoSE shows sizes as "156.5 KB".
export function formatFileSize(bytes: number): string {
  if (bytes < BYTES_PER_KB) return `${bytes} B`;
  if (bytes < BYTES_PER_MB) return `${(bytes / BYTES_PER_KB).toFixed(1)} KB`;
  return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
}

// "Add attachments" section of the ServiceNow catalog form, modelled on the
// RoSE catalog item widget: paperclip header, drag-and-drop zone, and a file
// list of thumbnail + "name (size)" + remove. RDS supplies the drop zone and
// the queue; the list is ours because RDS's row shows an upload progress bar,
// and nothing uploads from here. The parent sends the queued files
// base64-encoded inside the /offeringFulfillmentRequest submit
// (`requestDetails.attachments`). Files over 25 MB are refused before they
// reach the queue, as RoSE does.
@Component({
  selector: 'inexp-catalog-attachments',
  imports: [
    RdsIconButtonComponent,
    RdsIconComponent,
    RdsUploadComponent,
    RdsUploadErrorDirective,
  ],
  templateUrl: './catalog-attachments.component.html',
  styleUrl: './catalog-attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'inexp-catalog-attachments',
  },
})
export class CatalogAttachmentsComponent {
  readonly disabled = input(false);
  // `mandatoryAttachment` on the item: shows the asterisk. The parent owns
  // the submit check and passes the resulting message back as `error`.
  readonly required = input(false);
  readonly error = input<string | null>(null);
  readonly filesChange = output<File[]>();

  readonly TEXTS = CATALOG_ATTACHMENT_TEXTS;

  readonly uploader = new RdsFileUploader({
    url: '',
    autoUpload: false,
    multiple: true,
    maxFileSize: MAX_FILE_SIZE_BYTES,
  });

  readonly rows = signal<CatalogAttachmentRow[]>([]);
  // Files refused on the last pick (too large). Cleared on the next pick.
  readonly rejectionMessages = signal<string[]>([]);
  // Polite announcement of adds / removes for screen readers.
  readonly liveMessage = signal('');

  // Refuses oversized files before RDS queues them, so they never appear in
  // the list or reach the payload.
  readonly beforeAdd = (files: File[]): File[] => {
    const accepted = files.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    this.rejectionMessages.set(
      files
        .filter((f) => f.size > MAX_FILE_SIZE_BYTES)
        .map((f) => this.TEXTS.FILE_TOO_LARGE(f.name)),
    );
    if (accepted.length > 0) {
      this.liveMessage.set(this.TEXTS.FILES_ADDED(accepted.map((f) => f.name)));
    }
    return accepted;
  };

  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _document = inject(DOCUMENT);
  private readonly _injector = inject(Injector);

  constructor() {
    // Drop-zone wording as on RoSE ("Choose a file or drag it here.").
    // The intl service is a root singleton; this is the app's only rds-upload.
    const intl = inject(RdsUploadIntlService);
    intl.setTranslation('dropZoneChooseFiles', this.TEXTS.CHOOSE_FILE);
    intl.setTranslation('dropZoneOrDropFiles', this.TEXTS.OR_DRAG_IT_HERE);

    this.uploader.queue$.pipe(takeUntilDestroyed()).subscribe((queue) => {
      const shrank = queue.length < this.rows().length;
      this._syncRows(queue);
      this.filesChange.emit(queue.map((item) => item.file));
      if (shrank) this._restoreFocusAfterRemove();
    });

    inject(DestroyRef).onDestroy(() => this._revokeThumbnails(this.rows()));

    // Patches RDS's drop zone after every render:
    //  - RDS renders it as a <button> with no type. Inside the catalog <form>
    //    that defaults to type="submit", so opening the picker would also
    //    submit the request.
    //  - RDS links errors via aria-errormessage without aria-invalid (and
    //    comma-joins the ids), so screen readers skip them. Describe the
    //    button with our error instead. RDS assigns that element's id itself
    //    (RdsUploadErrorDirective.setId), so read it back from the DOM.
    afterRenderEffect(() => {
      this.error();
      const dropZone = this._dropZone();
      if (!dropZone) return;
      dropZone.setAttribute('type', 'button');
      const errorEl = this._host.nativeElement.querySelector(ERROR_SELECTOR);
      if (errorEl?.id) {
        dropZone.setAttribute('aria-describedby', errorEl.id);
        dropZone.setAttribute('aria-invalid', 'true');
      } else {
        dropZone.removeAttribute('aria-describedby');
        dropZone.removeAttribute('aria-invalid');
      }
    });
  }

  remove(row: CatalogAttachmentRow): void {
    this.liveMessage.set(this.TEXTS.FILE_REMOVED(row.name));
    this.uploader.removeFromQueue(row.item);
  }

  // Lets the form move keyboard focus here after a failed submit.
  focusPicker(): void {
    this._dropZone()?.focus();
  }

  // Rebuilds the rows from the RDS queue, keeping existing thumbnails and
  // revoking those of removed files.
  private _syncRows(queue: RdsFileItem[]): void {
    const previous = new Map(this.rows().map((row) => [row.item, row]));
    const next = queue.map(
      (item) =>
        previous.get(item) ?? {
          item,
          name: item.file.name,
          size: formatFileSize(item.file.size),
          thumbnailUrl: item.file.type.startsWith('image/')
            ? URL.createObjectURL(item.file)
            : null,
        },
    );
    this._revokeThumbnails(this.rows().filter((row) => !queue.includes(row.item)));
    this.rows.set(next);
  }

  private _revokeThumbnails(rows: CatalogAttachmentRow[]): void {
    for (const row of rows) {
      if (row.thumbnailUrl) URL.revokeObjectURL(row.thumbnailUrl);
    }
  }

  private _dropZone(): HTMLButtonElement | null {
    return this._host.nativeElement.querySelector<HTMLButtonElement>(DROP_ZONE_SELECTOR);
  }

  // The remove button lives in the row that disappears, so focus would drop
  // to <body>. Send it back to the picker instead.
  private _restoreFocusAfterRemove(): void {
    afterNextRender(
      () => {
        const active = this._document.activeElement;
        if (!active || active === this._document.body || !active.isConnected) {
          this.focusPicker();
        }
      },
      { injector: this._injector },
    );
  }
}
